import { valibotResolver } from '@hookform/resolvers/valibot';
import { useParams } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  maxLength,
  minLength,
  object,
  optional,
  pipe,
  string,
  trim,
  type InferInput as ValibotInput,
} from 'valibot';
import { useCreateRequest } from '@/api/requests/queries';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PRIORITIES, STATUSES } from '../-utils/config';

const FormSchema = object({
  name: pipe(
    string(),
    minLength(1, 'Please enter Request title'),
    maxLength(50, 'Title cannot exceed 50 symbols'),
    trim(),
  ),
  description: pipe(
    string(),
    minLength(10, 'Please enter Request description (At least 10 symbols)'),
    maxLength(500, 'Title cannot exceed 500 symbols'),
    trim(),
  ),
  status: optional(string()),
  priority: optional(string()),
});

export type FormDataType = ValibotInput<typeof FormSchema>;

export const CreateRequestDialog = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const mutation = useCreateRequest();

  const form = useForm<FormDataType>({
    resolver: valibotResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      status: 'pending',
      priority: 'low',
    },
  });

  useEffect(() => {
    if (!isDialogOpen) form.reset();
  }, [isDialogOpen, form]);

  const onSubmit = (data: FormDataType) => {
    return mutation.mutateAsync(
      { data, projectId },
      {
        onSuccess: () => {
          setIsDialogOpen(false);
          toast.success('Request has been created');
        },
      },
    );
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="default">
          <PlusIcon size={20} strokeWidth={3} />
          <span className="hidden md:block">Create Request</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Request</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col space-y-6">
              <div>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Request title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Request description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(STATUSES).map((status) => (
                                <Fragment key={status.status}>
                                  <SelectItem value={status.status}>
                                    <div className="flex items-center gap-2">
                                      <status.Icon className={status.iconClassName} size={16} />
                                      {status.label}
                                    </div>
                                  </SelectItem>
                                  {status.status === 'rejected' && <SelectSeparator />}
                                </Fragment>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Priority</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select priority" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(PRIORITIES).map((priority) => (
                                <SelectItem key={priority.status} value={priority.status}>
                                  <div className="flex items-center gap-2">
                                    <priority.Icon size={16} />
                                    {priority.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
              <Button
                className="self-end"
                type="submit"
                disabled={form.formState.isSubmitting || !form.formState.isDirty}
              >
                Submit
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
